import React from 'react';
import EditorCardHeader from './EditorCardHeader';
import EditorFormattingToolbar from './EditorFormattingToolbar';
import EditorCardFooter from './EditorCardFooter';
import { stripPrefix } from '../../team-lead/analysis/utils/helperUtils';
import { exportQueriesToExcel } from '../../services/rfpService';
import UnifiedTiptapEditor from './UnifiedTiptapEditor';
import { Download, Clock, AlertTriangle, Columns, Eye } from 'lucide-react';
import { markdownToHTML } from '../../team-lead/analysis/utils/markdownUtils';
import { CommercialsSectionButton, RaciLegend, AutoSaveBanner } from './components/ContentEditorPanels';
import { useScrollToTop } from '../../../../utils/scrollUtils';
import './ContentEditor.css';

// ─── HTML/Markdown/Structured content formatter ──────────────────────────────
const formatContent = (content) => {
    if (!content) return '';
    if (typeof content !== 'string') {
        if (Array.isArray(content)) {
            if (content.length > 0 && typeof content[0] === 'object') {
                const headers = Object.keys(content[0]);
                const headerRow = `| ${headers.join(' | ')} |`;
                const separatorRow = `| ${headers.map(() => '---').join(' | ')} |`;
                const dataRows = content.map(row =>
                    `| ${headers.map(h => String(row[h] ?? '')).join(' | ')} |`
                );
                return markdownToHTML([headerRow, separatorRow, ...dataRows].join('\n'));
            }
            return markdownToHTML(content.map(c => typeof c === 'object' ? JSON.stringify(c) : String(c)).join('\n\n'));
        }
        if (typeof content === 'object') {
            return markdownToHTML(content.content || JSON.stringify(content, null, 2));
        }
        return String(content);
    }
    if (/<[a-z][\s\S]*>/i.test(content)) {
        let processed = content;
        processed = processed.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
        processed = processed.replace(/(?<!\*)\*(?!\*)([^*\n]+?)\*(?!\*)/g, '<em>$1</em>');
        processed = processed.replace(/^(#{1,6})\s+(.+)$/gm, (_, hashes, title) => {
            const level = Math.min(hashes.length + 1, 4);
            return `<h${level}>${title}</h${level}>`;
        });
        return processed;
    }
    if (content.includes('|')) {
        const lines = content.split('\n').map(l => l.trim()).filter(Boolean);
        const pipeLines = lines.filter(line => line.startsWith('|') && line.split('|').length > 2);
        if (pipeLines.length > 0) return markdownToHTML(pipeLines.join('\n'));
    }
    return markdownToHTML(content);
};

/**
 * ContentEditor — Phase 3 (Final Cleanup & Sync).
 * - All editing now goes through UnifiedTiptapEditor (Tiptap).
 * - Undo/Redo wired to Tiptap's native history.
 * - handleSidePanelEdit feeds updated markdown to server on "Update".
 * - Dead block-based code removed.
 */
const ContentEditor = ({
    activeSection,
    selectedSection,
    selectedCell,
    editableData,
    isEditingContent,
    isUpdating,
    handleUpdate,
    startEditing,
    setShowAIModal,
    handleSidePanelEdit,
    cancelEditing,
    renderKeyInformation,
    isManager,
    setStageModal,
    setStageError,
    handleToggleSectionApproval,
    handleSubmitToLead,
    isSubmittingToLead,
    isApprovingSection = false,
    setShowVersionHistory,
    isSidebarCollapsed,
    setIsSidebarCollapsed,
    assignments = {},
    rfpId,
    fetchRFPData,
    isNoGo = false,
    isTeamLeadViewOnly = false,
    isSectionReadOnly = false,
    // ── Collaboration props (all optional) ──────────────────────────
    collabYdoc = null,
    collabProvider = null,
    collabUser = null,
    collabStatus = null,
    collaborators = [],
    collabReady = false,
    collabRetry = null,
    onLocalEdit = null,
    // ── Global sync ──────────────────────────────────────
    onCancelSync = null,
}) => {
    const sectionTitle = selectedSection?.label || selectedSection?.key || stripPrefix(activeSection);
    const tiptapRef = React.useRef(null);
    const cardBodyRef = React.useRef(null);

    const [isExporting, setIsExporting] = React.useState(false);
    const [isTiptapTableActive, setIsTiptapTableActive] = React.useState(false);
    const [isFullScreen, setIsFullScreen] = React.useState(false);

    // Automatically reset scroll position when section changes
    useScrollToTop([activeSection]);
    React.useEffect(() => {
        if (cardBodyRef.current) {
            cardBodyRef.current.scrollTop = 0;
        }
    }, [activeSection]);

    // Unsaved changes warning logic
    const initialContentRef = React.useRef('');
    React.useEffect(() => {
        if (isEditingContent) {
            initialContentRef.current = formatContent(editableData[activeSection] || '');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEditingContent, activeSection]);

    // Leaving edit mode never discards work: the parent persists any edits you made
    // (see leaveEditing in RFPAnalysisResult) so there is nothing to confirm.
    const handleCancelWithWarning = async () => {
        await cancelEditing();
    };

    const handleSectionSubmit = () => {
        if (typeof handleSubmitToLead === 'function') {
            handleSubmitToLead();
        }
    };

    // Resolve section status, revision notes, and submitted draft
    const statusObj = selectedSection?.status && typeof selectedSection?.status === 'object' ? selectedSection.status : {};
    const statusCode = Number(statusObj.code || selectedSection?.status_code || 0);
    const statusStr = String(statusObj.label || selectedSection?.status || '').toLowerCase();
    const isSubmitted = statusCode === 53 || statusStr.includes('submitted');
    const isApproved = statusCode === 54 || statusStr.includes('approved');
    const isRevisionNeeded = statusCode === 55 || statusStr.includes('revision');
    const isExpired = statusCode === 56 || statusStr.includes('expired');
    const isAutoClosed = statusCode === 57 || statusStr.includes('auto-closed') || statusStr.includes('closed');
    const isSectionLocked = isSectionReadOnly || isSubmitted || isApproved || isExpired || isAutoClosed || isTeamLeadViewOnly || isNoGo;

    // Two copies of every section exist:
    //   - APPROVED (main) content  -> what READ mode shows, for every role, until the Team Lead
    //                                 approves someone's work.
    //   - DRAFT (working copy)     -> what the EDITOR shows and what people save/submit.
    // Read mode takes the approved text from the section object (refreshed from the server) and
    // NOT from editableData, because editableData carries the live draft while someone is editing.
    const sectionObjHasContent = Boolean(selectedSection) &&
        (selectedSection.content !== undefined || selectedSection.text !== undefined);
    const approvedContent = sectionObjHasContent
        ? (selectedSection.content || selectedSection.text || '')
        : (editableData[activeSection] || editableData[selectedSection?.label] || editableData[selectedSection?.key] || '');

    // Working content used by the editor / save: live edits first, then the section object.
    const resolvedSectionContent =
        editableData[activeSection] ||
        editableData[selectedSection?.label] ||
        editableData[selectedSection?.key] ||
        selectedSection?.content ||
        selectedSection?.text ||
        '';

    // What the editor starts from if the shared live document turns out to be empty: the saved
    // draft when there is one, otherwise the approved text. (If the live document already has
    // content it is the draft and is used as-is.)
    const draftFromServer = (typeof selectedSection?.draft_content === 'string' && selectedSection.draft_content.trim())
        ? selectedSection.draft_content
        : '';
    const seedContentForEditor = formatContent(draftFromServer || approvedContent);

    const handleSaveWithEditorContent = async () => {
        let contentToSave = editableData[activeSection];
        if (tiptapRef.current && isEditingContent) {
            const editorHtml = tiptapRef.current.getHTML();
            if (editorHtml) {
                contentToSave = editorHtml;
                if (typeof handleSidePanelEdit === 'function') {
                    handleSidePanelEdit(editorHtml);
                }
            }
        }
        if (typeof handleUpdate === 'function') {
            await handleUpdate(contentToSave);
        }
    };

    const submittedDraft = selectedSection?.submitted_draft;
    const revisionNotes = selectedSection?.revision_notes || selectedSection?.notes;
    const [compareViewMode, setCompareViewMode] = React.useState('split'); // 'split' | 'draft' | 'approved'

    // Subscribe to Tiptap selection so toolbar enables/disables table buttons
    React.useEffect(() => {
        const editor = tiptapRef.current;
        if (!editor) return;
        const handler = () => setIsTiptapTableActive(editor.isActive('table'));
        editor.on('selectionUpdate', handler);
        editor.on('transaction', handler);
        return () => { editor.off('selectionUpdate', handler); editor.off('transaction', handler); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tiptapRef.current]);

    // ── Format / Table actions via Tiptap native commands ──────────────────
    const handleFormatAction = (action, payload = null) => {
        if (isTeamLeadViewOnly) return;
        const editor = tiptapRef.current;
        if (!editor) return;

        const setListStyle = (type, attrName, attrValue) => {
            if (editor.isActive('listItem')) {
                if (type === 'bulletList') editor.chain().focus().liftListItem('listItem').toggleBulletList().updateAttributes(type, { [attrName]: attrValue }).run();
                else editor.chain().focus().liftListItem('listItem').toggleOrderedList().updateAttributes(type, { [attrName]: attrValue }).run();
            } else {
                if (type === 'bulletList') editor.chain().focus().toggleBulletList().updateAttributes(type, { [attrName]: attrValue }).run();
                else editor.chain().focus().toggleOrderedList().updateAttributes(type, { [attrName]: attrValue }).run();
            }
        };

        if (action === 'insertTable') { const { rows, cols } = payload || { rows: 3, cols: 3 }; editor.chain().focus().insertTable({ rows, cols, withHeaderRow: true }).run(); return; }
        if (action.startsWith('OrderedList:')) { setListStyle('orderedList', 'type', action.split(':')[1]); return; }
        if (action.startsWith('BulletList:')) {
            const styleMap = { disc: 'disc', circle: 'circle', square: 'square', dash: "'-'", arrow: "'➜'", diamond: "'◆'" };
            setListStyle('bulletList', 'style', styleMap[action.split(':')[1]] || 'disc');
            return;
        }

        switch (action) {
            case 'Bold': editor.chain().focus().toggleBold().run(); break;
            case 'Italic': editor.chain().focus().toggleItalic().run(); break;
            case 'Underline': editor.chain().focus().toggleUnderline().run(); break;
            case 'Strike': editor.chain().focus().toggleStrike().run(); break;
            case 'paragraph': editor.chain().focus().setParagraph().run(); break;
            case 'heading1': editor.chain().focus().toggleHeading({ level: 1 }).run(); break;
            case 'heading2': editor.chain().focus().toggleHeading({ level: 2 }).run(); break;
            case 'heading3': editor.chain().focus().toggleHeading({ level: 3 }).run(); break;
            case 'Bullet List': editor.isActive('orderedList') ? editor.chain().focus().liftListItem('listItem').toggleBulletList().run() : editor.chain().focus().toggleBulletList().run(); break;
            case 'Numbered List': editor.isActive('bulletList') ? editor.chain().focus().liftListItem('listItem').toggleOrderedList().run() : editor.chain().focus().toggleOrderedList().run(); break;
            case 'Add Paragraph': editor.chain().focus().setParagraph().run(); break;
            case 'addRow': editor.chain().focus().addRowAfter().run(); break;
            case 'addColumn': editor.chain().focus().addColumnAfter().run(); break;
            case 'deleteRow': editor.chain().focus().deleteRow().run(); break;
            case 'deleteColumn': editor.chain().focus().deleteColumn().run(); break;
            default: break;
        }
    };

    const handleUndo = () => tiptapRef.current?.chain().focus().undo().run();
    const handleRedo = () => tiptapRef.current?.chain().focus().redo().run();

    const handleExport = async () => {
        if (!rfpId || isExporting) return;
        try {
            setIsExporting(true);
            const response = await exportQueriesToExcel(rfpId);
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Clarifications_Queries_${rfpId}.xlsx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error('Export failed:', error);
        } finally {
            setIsExporting(false);
        }
    };

    const isCommercialsSection = activeSection === 'chapter_commercials' || activeSection?.toLowerCase() === 'commercials' || selectedSection?.key === 'chapter_commercials';
    const isRaciSection = (activeSection || '').toUpperCase().includes('RACI') || (sectionTitle || '').toUpperCase().includes('RACI');
    const isQueriesSection = isManager && (activeSection?.toUpperCase().includes('CLARIFICATIONS') || activeSection?.toUpperCase().includes('QUERIES'));

    return (
        <div className="final-main-content">
            <div className={`final-content-card ${isFullScreen ? 'fullscreen-active' : ''}`}>
                <EditorCardHeader
                    sectionTitle={sectionTitle}
                    isSidebarCollapsed={isSidebarCollapsed}
                    setIsSidebarCollapsed={setIsSidebarCollapsed}
                    editableData={editableData}
                    isEditingContent={isEditingContent}
                    isUpdating={isUpdating}
                    isManager={isManager}
                    handleUpdate={handleSaveWithEditorContent}
                    startEditing={() => (typeof startEditing === 'function' ? startEditing(resolvedSectionContent) : null)}
                    cancelEditing={handleCancelWithWarning}
                    setShowAIModal={setShowAIModal}
                    setShowVersionHistory={setShowVersionHistory}
                    isNoGo={isNoGo}
                    isTeamLeadViewOnly={isTeamLeadViewOnly}
                    assignments={assignments}
                    selectedSection={selectedSection}
                    onCancelSync={onCancelSync}
                />

                {isEditingContent && (
                    <EditorFormattingToolbar
                        editor={tiptapRef.current}
                        editorRef={tiptapRef}
                        isTableActive={isTiptapTableActive}
                        handleFormatAction={handleFormatAction}
                        undo={handleUndo}
                        redo={handleRedo}
                        rfpId={rfpId}
                        isFullScreen={isFullScreen}
                        setIsFullScreen={setIsFullScreen}
                    />
                )}

                <div ref={cardBodyRef} className={`final-card-body ${isEditingContent ? 'editing-mode' : ''}`}>
                    {activeSection === 'Key Information' ? (
                        renderKeyInformation()
                    ) : (
                        <>
                            {/* Revision Needed Callout */}
                            {isRevisionNeeded && revisionNotes && (
                                <div className="revision-notes-callout" style={{
                                    backgroundColor: '#FEF2F2',
                                    border: '1px solid #FECACA',
                                    borderRadius: '8px',
                                    padding: '12px 16px',
                                    marginBottom: '16px',
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    gap: '12px'
                                }}>
                                    <AlertTriangle size={18} color="#DC2626" style={{ marginTop: '2px', flexShrink: 0 }} />
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: 700, color: '#991B1B', fontSize: '0.85rem', marginBottom: '4px' }}>
                                            Revision Requested by Team Lead
                                        </div>
                                        <div style={{ color: '#7F1D1D', fontSize: '0.85rem', lineHeight: '1.4' }}>
                                            {revisionNotes}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Employee Waiting for TL Review Banner */}
                            {!isManager && isSubmitted && (
                                <div className="waiting-review-banner" style={{
                                    backgroundColor: '#FFFBEB',
                                    border: '1px solid #FDE68A',
                                    borderRadius: '8px',
                                    padding: '12px 16px',
                                    marginBottom: '16px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                    color: '#92400E'
                                }}>
                                    <Clock size={18} color="#D97706" style={{ flexShrink: 0 }} />
                                    <div style={{ flex: 1 }}>
                                        <span style={{ fontWeight: 700, fontSize: '0.85rem', marginRight: '8px' }}>
                                            Waiting for Team Lead review
                                        </span>
                                        <span style={{ fontSize: '0.8rem', color: '#B45309' }}>
                                            This section draft was submitted and is now locked pending approval or revision request from your Team Lead.
                                        </span>
                                    </div>
                                </div>
                            )}

                            {/* Team Lead Side-by-Side Review Mode */}
                            {isManager && isSubmitted && submittedDraft ? (
                                <div className="review-compare-wrapper" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        padding: '8px 14px',
                                        background: '#EEF2FF',
                                        borderRadius: '8px',
                                        border: '1px solid #C7D2FE',
                                        flexWrap: 'wrap',
                                        gap: '8px'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span style={{ fontSize: '0.85rem', fontWeight: '700', color: '#4338CA' }}>
                                                Section Review Mode
                                            </span>
                                            <span style={{ fontSize: '0.75rem', color: '#6366F1' }}>
                                                · Side-by-side comparison of approved content and employee submitted draft
                                            </span>
                                        </div>
                                        <div style={{ display: 'flex', background: '#FFFFFF', padding: '2px', borderRadius: '6px', border: '1px solid #E0E7FF' }}>
                                            <button
                                                onClick={() => setCompareViewMode('split')}
                                                style={{
                                                    background: compareViewMode === 'split' ? '#4F46E5' : 'transparent',
                                                    color: compareViewMode === 'split' ? '#FFFFFF' : '#475569',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    padding: '4px 10px',
                                                    fontSize: '0.75rem',
                                                    fontWeight: '600',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '4px'
                                                }}
                                            >
                                                <Columns size={12} />
                                                Side-by-Side
                                            </button>
                                            <button
                                                onClick={() => setCompareViewMode('draft')}
                                                style={{
                                                    background: compareViewMode === 'draft' ? '#4F46E5' : 'transparent',
                                                    color: compareViewMode === 'draft' ? '#FFFFFF' : '#475569',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    padding: '4px 10px',
                                                    fontSize: '0.75rem',
                                                    fontWeight: '600',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                Submitted Draft
                                            </button>
                                            <button
                                                onClick={() => setCompareViewMode('approved')}
                                                style={{
                                                    background: compareViewMode === 'approved' ? '#4F46E5' : 'transparent',
                                                    color: compareViewMode === 'approved' ? '#FFFFFF' : '#475569',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    padding: '4px 10px',
                                                    fontSize: '0.75rem',
                                                    fontWeight: '600',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                Current Approved
                                            </button>
                                        </div>
                                    </div>

                                    {compareViewMode === 'split' ? (
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', minHeight: '420px' }}>
                                            {/* Left: Approved Content */}
                                            <div style={{ border: '1px solid #E2E8F0', borderRadius: '8px', overflow: 'hidden', display: 'flex', flexDirection: 'column', background: '#F8FAFC' }}>
                                                <div style={{ padding: '8px 14px', background: '#F1F5F9', borderBottom: '1px solid #E2E8F0', fontSize: '0.75rem', fontWeight: '700', color: '#475569', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <span>CURRENT APPROVED CONTENT (MAIN DOC)</span>
                                                    <span style={{ fontSize: '0.65rem', padding: '2px 6px', background: '#E2E8F0', borderRadius: '4px' }}>Active</span>
                                                </div>
                                                <div className="seamless-tiptap-content" style={{ padding: '16px', flex: 1, overflowY: 'auto' }}>
                                                    {editableData[activeSection] ? (
                                                        <div dangerouslySetInnerHTML={{ __html: formatContent(editableData[activeSection]) }} />
                                                    ) : (
                                                        <span style={{ color: '#94A3B8', fontStyle: 'italic' }}>No approved content in main RFP document yet.</span>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Right: Submitted Draft */}
                                            <div style={{ border: '1.5px solid #818CF8', borderRadius: '8px', overflow: 'hidden', display: 'flex', flexDirection: 'column', background: '#FFFFFF', boxShadow: '0 4px 12px rgba(99, 102, 241, 0.08)' }}>
                                                <div style={{ padding: '8px 14px', background: '#EEF2FF', borderBottom: '1px solid #C7D2FE', fontSize: '0.75rem', fontWeight: '700', color: '#4338CA', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <span>SUBMITTED DRAFT (PENDING REVIEW)</span>
                                                    <span style={{ fontSize: '0.65rem', padding: '2px 6px', background: '#6366F1', color: '#FFFFFF', borderRadius: '4px' }}>Under Review</span>
                                                </div>
                                                <div className="seamless-tiptap-content" style={{ padding: '16px', flex: 1, overflowY: 'auto' }}>
                                                    <div dangerouslySetInnerHTML={{ __html: formatContent(submittedDraft) }} />
                                                </div>
                                            </div>
                                        </div>
                                    ) : compareViewMode === 'draft' ? (
                                        <div style={{ border: '1.5px solid #818CF8', borderRadius: '8px', overflow: 'hidden', background: '#FFFFFF', minHeight: '350px' }}>
                                            <div style={{ padding: '8px 14px', background: '#EEF2FF', borderBottom: '1px solid #C7D2FE', fontSize: '0.75rem', fontWeight: '700', color: '#4338CA' }}>
                                                SUBMITTED DRAFT (PENDING REVIEW)
                                            </div>
                                            <div className="seamless-tiptap-content" style={{ padding: '16px' }}>
                                                <div dangerouslySetInnerHTML={{ __html: formatContent(submittedDraft) }} />
                                            </div>
                                        </div>
                                    ) : (
                                        <div style={{ border: '1px solid #E2E8F0', borderRadius: '8px', overflow: 'hidden', background: '#F8FAFC', minHeight: '350px' }}>
                                            <div style={{ padding: '8px 14px', background: '#F1F5F9', borderBottom: '1px solid #E2E8F0', fontSize: '0.75rem', fontWeight: '700', color: '#475569' }}>
                                                CURRENT APPROVED CONTENT (MAIN DOC)
                                            </div>
                                            <div className="seamless-tiptap-content" style={{ padding: '16px' }}>
                                                {editableData[activeSection] ? (
                                                    <div dangerouslySetInnerHTML={{ __html: formatContent(editableData[activeSection]) }} />
                                                ) : (
                                                    <span style={{ color: '#94A3B8', fontStyle: 'italic' }}>No approved content in main RFP document yet.</span>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ) : (isEditingContent && !isSectionLocked) ? (
                                <div className="unified-editor-container">
                                    {collabStatus === 'connecting' && !collabYdoc ? (
                                        <div className="unified-editor-loading-state" style={{ minHeight: '350px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '12px', background: '#FFFFFF', borderRadius: '8px', border: '1px solid #E2E8F0' }}>
                                            <div style={{ width: '28px', height: '28px', border: '3px solid #E0E7FF', borderTopColor: '#4F46E5', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                                            <span style={{ fontSize: '0.875rem', fontWeight: 600, color: '#475569' }}>
                                                Joining collaborative editing session...
                                            </span>
                                        </div>
                                    ) : (
                                        <UnifiedTiptapEditor
                                            key={`editor-${selectedSection?.id || selectedSection?.section_id || activeSection}-${Boolean(collabYdoc && collabProvider)}`}
                                            htmlContent={formatContent(resolvedSectionContent)}
                                            seedContent={seedContentForEditor}
                                            onChange={(html) => { if (typeof handleSidePanelEdit === 'function') handleSidePanelEdit(html); }}
                                            editorRef={tiptapRef}
                                            isFullScreen={isFullScreen}
                                            rfpId={rfpId}
                                            collabYdoc={collabYdoc}
                                            collabProvider={collabProvider}
                                            collabUser={collabUser}
                                            collabStatus={collabStatus}
                                            collaborators={collaborators}
                                            collabReady={collabReady}
                                            collabRetry={collabRetry}
                                            onLocalEdit={onLocalEdit}
                                            isReadOnly={false}
                                        />
                                    )}
                                </div>
                            ) : (
                                <div className="final-view-area" style={{ position: 'relative' }}>
                                    {isQueriesSection && (
                                        <button className={`section-export-inline-btn ${isExporting ? 'exporting' : ''}`} onClick={handleExport} disabled={isExporting} title="Export section data to Excel">
                                            <Download size={14} className={isExporting ? 'animate-bounce' : ''} />
                                            <span>{isExporting ? 'Exporting...' : 'Export'}</span>
                                        </button>
                                    )}
                                    {isCommercialsSection && !isNoGo && (
                                        <CommercialsSectionButton rfpId={rfpId} editableData={editableData} fetchRFPData={fetchRFPData} />
                                    )}
                                    {/* The employee workspace shows the shared draft, which is not the approved main document yet. */}
                                    {!isManager && !isApproved && (
                                        <div
                                            style={{
                                                display: 'inline-flex', alignItems: 'center', gap: '6px',
                                                margin: '0 0 8px 0', padding: '3px 10px', borderRadius: '6px',
                                                background: '#FFF2ED', border: '1px solid #FFD2C2',
                                                color: '#D93E1A', fontSize: '0.72rem', fontWeight: 700,
                                            }}
                                            title="This is the shared draft. The main document changes only after the Team Lead approves."
                                        >
                                            Draft – not yet approved
                                        </div>
                                    )}
                                    <div className="content-padding-redesign seamless-tiptap-content">
                                        <div dangerouslySetInnerHTML={{ __html: formatContent(approvedContent) }} />
                                    </div>
                                </div>
                            )}
                            {isRaciSection && <RaciLegend />}
                        </>
                    )}
                </div>

                {isEditingContent && <AutoSaveBanner />}

                <div className="final-card-footer">
                    <EditorCardFooter
                        editableData={editableData}
                        activeSection={activeSection}
                        selectedSection={selectedSection}
                        sectionTitle={sectionTitle}
                        assignments={assignments}
                        isManager={isManager}
                        handleUpdate={handleSaveWithEditorContent}
                        handleSubmitToLead={handleSectionSubmit}
                        isSubmittingToLead={isSubmittingToLead}
                        handleToggleSectionApproval={handleToggleSectionApproval}
                        isApprovingSection={isApprovingSection}
                        setStageModal={setStageModal}
                        setStageError={setStageError}
                        isNoGo={isNoGo}
                        isTeamLeadViewOnly={isTeamLeadViewOnly}
                    />
                </div>
            </div>
        </div>
    );
};

export default ContentEditor;
